// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The procedural art engine (M3-12, 04 §13).
//
// Every shipped pixel is drawn here. Zero image files ship, which is not a purity exercise:
// it removes all asset licensing, removes the Store's third-party licence manifest, and gives
// the Machine Builder a drawing vocabulary with no upload-moderation surface at all.
//
// **Determinism is the hard requirement, not the aesthetics.** A ghost replay has to look like
// the game it recorded, and two players comparing a machine have to be looking at the same
// machine, so the same program and the same seed must produce the same pixels on a laptop and
// on a Quest. That rules out `Math.random` for the starfields and rules out anything that
// depends on canvas defaults, so every op sets what it uses.
//
// It takes a `Canvas2D` rather than a `CanvasRenderingContext2D` on purpose: the interface
// below is the subset actually used, which means the whole engine runs — and is tested —
// headlessly against a recording stub, and the browser passes the real thing.

import { ART_OPS } from '@core/machine/types'
import type { ArtOp } from '@core/machine/types'
import { Rng } from '@core/rng/Rng'
import {isPrintedBumperMarker} from './componentArtwork'
import { strokeWidth, typeset, type TextAlign } from '@core/art/typeset'
import { GLYPH_METRICS } from '@core/art/glyphs'

/**
 * The drawing surface, as much of it as the vocabulary needs.
 *
 * Deliberately structural rather than `CanvasRenderingContext2D`: a browser context satisfies
 * it, and so does a two-hundred-line recorder in a test.
 */
export interface Canvas2D {
  fillStyle: string | CanvasGradientLike
  strokeStyle: string | CanvasGradientLike
  lineWidth: number
  globalAlpha: number
  /** Round, always. The typeface is monoline strokes and a butt cap leaves every join notched. */
  lineCap: string
  lineJoin: string
  shadowBlur: number
  shadowColor: string
  save(): void
  restore(): void
  translate(x: number, y: number): void
  rotate(angle: number): void
  scale(x: number, y: number): void
  beginPath(): void
  moveTo(x: number, y: number): void
  lineTo(x: number, y: number): void
  arc(x: number, y: number, r: number, from: number, to: number): void
  closePath(): void
  fill(): void
  stroke(): void
  fillRect(x: number, y: number, w: number, h: number): void
  createLinearGradient(x0: number, y0: number, x1: number, y1: number): CanvasGradientLike
  createRadialGradient(x0: number, y0: number, r0: number, x1: number, y1: number, r1: number): CanvasGradientLike
}

export interface CanvasGradientLike {
  addColorStop(offset: number, colour: string): void
}

export interface ArtSurface {
  readonly resolution?: readonly [number, number]
  readonly program: readonly Record<string, unknown>[]
}

export interface ArtRenderOptions {
  readonly width: number
  readonly height: number
  /** From `art.seed`. Every stochastic op derives its stream from this. */
  readonly seed: number
  /**
   * Treat the surface's normalised `y` as running up the playfield rather than down the canvas.
   *
   * Set for a playfield, left alone for anything else. See `Env.yUp`.
   */
  readonly yUp?: boolean
  /** Runtime component lamps replace exact original painted bumper halos. */
  readonly componentSources?: boolean
}

/** Ops the engine can draw. Kept in step with `ART_OPS` by a test, not by discipline. */
const HANDLERS: Record<ArtOp, (ctx: Canvas2D, op: Record<string, unknown>, env: Env) => void> = {
  solid: opSolid,
  linearGradient: opLinearGradient,
  radialGradient: opRadialGradient,
  vignette: opVignette,
  starfield: opStarfield,
  rays: opRays,
  neonRing: opNeonRing,
  glowLine: opGlowLine,
  grid: opGrid,
  chevrons: opChevrons,
  streaks: opStreaks,
  text: opText,
  arcText: opArcText,
  laneLetters: opLaneLetters,
  label: opLabel,
  wordmark: opWordmark,
  arrow: opArrow,
  insert: opInsert,
  panel: opPanel,
  border: opBorder,
  dmdWindow: opDmdWindow,
  mark: opMark,
}

interface Env {
  readonly w: number
  readonly h: number
  /**
   * The surface's normalised `y` runs *up* the playfield, not down the canvas.
   *
   * A playfield art program addresses the machine the way the machine definition does — PH-001's
   * `+Y`, away from the player — so `laneLetters` at `y = 0.876` and lane rollovers at playfield
   * `y = +0.4000` (0.875 of the way up) mean the same place. A 2-D canvas draws `y` downward, so
   * the whole surface is drawn through one vertical flip and the result is a picture the right
   * way up.
   *
   * Glyphs are the exception and the reason this is a flag rather than a caller-side transform:
   * a flipped canvas draws upside-down letters. Every text op un-flips locally, so the layout
   * reads bottom-up and the words read left-to-right.
   *
   * A backglass has no such convention. It is a picture, drawn the way pictures are drawn.
   */
  readonly yUp: boolean
  /** One stream per surface, advanced by every op that draws something random. */
  readonly rng: Rng
}

/**
 * Execute one surface's program.
 *
 * Returns the ops it could not draw, which should always be empty — validation refuses an
 * unknown op at load. Reported rather than thrown so a Builder preview of a half-finished
 * machine still shows what it can.
 */
export function renderArtSurface(
  ctx: Canvas2D,
  surface: ArtSurface,
  options: ArtRenderOptions,
): string[] {
  const env: Env = {
    w: options.width,
    h: options.height,
    rng: new Rng(options.seed),
    yUp: options.yUp ?? false,
  }
  // One transform for the whole program rather than a conversion at every call site: an op that
  // forgot to convert would be off by however far it is from the middle, which looks like a
  // placement mistake rather than like a convention mismatch and is debugged as one.
  if (env.yUp) {
    ctx.save()
    ctx.translate(0, env.h)
    ctx.scale(1, -1)
  }
  const skipped: string[] = []
  for (const op of surface.program) {
    if(options.componentSources&&isPrintedBumperMarker(op))continue
    const name = op['op']
    if (typeof name !== 'string' || !(ART_OPS as readonly string[]).includes(name)) {
      skipped.push(String(name))
      continue
    }
    ctx.save()
    ctx.globalAlpha = num(op['alpha'], 1)
    HANDLERS[name as ArtOp](ctx, op, env)
    ctx.restore()
  }
  if (env.yUp) ctx.restore()
  return skipped
}

/**
 * Draw text in the product's own typeface.
 *
 * **There is no platform font here, and that is the point.** This used to be `ctx.fillText` with
 * `system-ui, sans-serif`, which meant NEON NOVA's wordmark rendered in Segoe UI on Windows, SF
 * Pro on a Mac and Roboto on Android — three different machines wearing the same name. A
 * machine's art is a property of the machine; a font that depends on the device reading it is
 * not one. `core/ts/art/` holds the letterforms and the layout as numbers, the C# core holds the
 * same numbers, and both clients draw the same word.
 *
 * On a `yUp` surface the canvas is flipped, so text drawn naively comes out upside down — and it
 * is *legible* upside down, which is why it survives a look at a screenshot. `typeset` takes that
 * as one sign rather than a per-glyph transform, so a flipped surface now costs no extra
 * save/scale/restore at all.
 *
 * The paint comes from `fillStyle`, which every caller already sets — including the wordmark's
 * gradient — so the colour of text stays one idea with one name.
 */
function glyph(
  ctx: Canvas2D,
  env: Env,
  text: string,
  x: number,
  y: number,
  size: number,
  align: TextAlign = 'center',
  weight: number = GLYPH_METRICS.weight,
): void {
  const runs = typeset(text, x, y, { size, align, baseline: 'middle', yDown: !env.yUp })
  if (runs.length === 0) return
  ctx.strokeStyle = ctx.fillStyle
  ctx.lineWidth = strokeWidth(size, weight)
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.beginPath()
  for (const run of runs) {
    ctx.moveTo(run[0]!, run[1]!)
    for (let i = 2; i + 1 < run.length; i += 2) ctx.lineTo(run[i]!, run[i + 1]!)
  }
  ctx.stroke()
}

// --- reading a program's parameters ------------------------------------------------
//
// Every accessor takes a default. An op with a missing parameter draws something sensible
// rather than throwing, because the Builder shows a preview while the author is still typing.

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback
}
function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.length > 0 ? v : fallback
}
/** A normalised point, 0…1 across the surface, resolved to pixels. */
function point(v: unknown, env: Env, fx: number, fy: number): readonly [number, number] {
  if (Array.isArray(v) && v.length === 2 && typeof v[0] === 'number' && typeof v[1] === 'number') {
    return [v[0] * env.w, v[1] * env.h]
  }
  return [fx * env.w, fy * env.h]
}
function pair(v: unknown, a: number, b: number): readonly [number, number] {
  if (Array.isArray(v) && v.length === 2 && typeof v[0] === 'number' && typeof v[1] === 'number') {
    return [v[0], v[1]]
  }
  return [a, b]
}
/** The larger surface dimension, so a normalised radius is round rather than elliptical. */
function span(env: Env): number {
  return env.w > env.h ? env.w : env.h
}

// --- the vocabulary ------------------------------------------------------------------

function opSolid(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  ctx.fillStyle = str(op['colour'], '#000000')
  ctx.fillRect(0, 0, env.w, env.h)
}

function opLinearGradient(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const [x0, y0] = point(op['from'], env, 0, 0)
  const [x1, y1] = point(op['to'], env, 0, 1)
  const g = ctx.createLinearGradient(x0, y0, x1, y1)
  g.addColorStop(0, str(op['colourFrom'], '#000000'))
  g.addColorStop(1, str(op['colourTo'], '#FFFFFF'))
  ctx.fillStyle = g
  ctx.fillRect(0, 0, env.w, env.h)
}

function opRadialGradient(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const [cx, cy] = point(op['centre'], env, 0.5, 0.5)
  const r = num(op['radius'], 0.75) * span(env)
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r)
  g.addColorStop(0, str(op['from'], '#0B0620'))
  g.addColorStop(1, str(op['to'], '#000000'))
  ctx.fillStyle = g
  ctx.fillRect(0, 0, env.w, env.h)
}

/** Darkens the edges. Cheap, and it is most of what makes a flat canvas read as lit. */
function opVignette(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const strength = num(op['strength'], 0.35)
  const g = ctx.createRadialGradient(env.w / 2, env.h / 2, span(env) * 0.25, env.w / 2, env.h / 2, span(env) * 0.72)
  g.addColorStop(0, 'rgba(0,0,0,0)')
  g.addColorStop(1, `rgba(0,0,0,${strength})`)
  ctx.fillStyle = g
  ctx.fillRect(0, 0, env.w, env.h)
}

function opStarfield(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const count = Math.min(4000, Math.max(0, Math.floor(num(op['count'], 500))))
  const [minSize, maxSize] = pair(op['sizeRange'], 0.6, 2.4)
  const colour = str(op['colour'], '#FFFFFF')
  ctx.fillStyle = colour
  for (let i = 0; i < count; i++) {
    const x = env.rng.nextFloat() * env.w
    const y = env.rng.nextFloat() * env.h
    const r = minSize + env.rng.nextFloat() * (maxSize - minSize)
    // Brightness varies, or a starfield reads as noise rather than as depth.
    ctx.globalAlpha = 0.25 + env.rng.nextFloat() * 0.75
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
  }
}

function opRays(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const [cx, cy] = point(op['centre'], env, 0.5, 0.5)
  const count = Math.min(256, Math.max(1, Math.floor(num(op['count'], 24))))
  const reach = num(op['reach'], 1.4) * span(env)
  const spread = (Math.PI * 2) / count
  ctx.fillStyle = str(op['colour'], '#FF3FA0')
  ctx.globalAlpha = num(op['alpha'], 0.22)
  for (let i = 0; i < count; i++) {
    const a = i * spread
    const half = spread * num(op['width'], 0.25)
    ctx.beginPath()
    ctx.moveTo(cx, cy)
    ctx.lineTo(cx + Math.cos(a - half) * reach, cy + Math.sin(a - half) * reach)
    ctx.lineTo(cx + Math.cos(a + half) * reach, cy + Math.sin(a + half) * reach)
    ctx.closePath()
    ctx.fill()
  }
}

/** A neon tube: a bright core inside a wide soft halo. Three passes, because one looks flat. */
function opNeonRing(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const [cx, cy] = point(op['centre'], env, 0.5, 0.5)
  const r = num(op['radius'], 0.2) * span(env)
  const colour = str(op['colour'], '#3FE0FF')
  const width = num(op['width'], 5)
  for (const [scale, alpha] of [[3.2, 0.16], [1.8, 0.32], [1.0, 1.0]] as const) {
    ctx.strokeStyle = colour
    ctx.lineWidth = width * scale
    ctx.globalAlpha = alpha
    ctx.shadowBlur = scale > 1 ? width * scale * 2 : 0
    ctx.shadowColor = colour
    ctx.beginPath()
    ctx.arc(cx, cy, r, 0, Math.PI * 2)
    ctx.stroke()
  }
}

function opGlowLine(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const [x0, y0] = point(op['from'], env, 0, 0.5)
  const [x1, y1] = point(op['to'], env, 1, 0.5)
  const colour = str(op['colour'], '#8B5CF6')
  const width = num(op['width'], 3)
  for (const [scale, alpha] of [[3.0, 0.2], [1.0, 1.0]] as const) {
    ctx.strokeStyle = colour
    ctx.lineWidth = width * scale
    ctx.globalAlpha = alpha
    ctx.shadowBlur = scale > 1 ? width * scale * 2 : 0
    ctx.shadowColor = colour
    ctx.beginPath()
    ctx.moveTo(x0, y0)
    ctx.lineTo(x1, y1)
    ctx.stroke()
  }
}

/** The synthwave horizon: lines converging on a vanishing point, spaced by a power law. */
function opGrid(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const region = Array.isArray(op['region']) && op['region'].length === 4
    ? (op['region'] as number[])
    : [0, 0.7, 1, 1]
  const x0 = region[0]! * env.w
  const y0 = region[1]! * env.h
  const x1 = region[2]! * env.w
  const y1 = region[3]! * env.h
  const rows = Math.min(64, Math.max(1, Math.floor(num(op['rows'], 10))))
  const columns = Math.min(128, Math.max(1, Math.floor(num(op['columns'], 16))))
  ctx.strokeStyle = str(op['colour'], '#8B5CF6')
  ctx.lineWidth = num(op['width'], 2)
  ctx.globalAlpha = num(op['alpha'], 0.5)

  for (let c = 0; c <= columns; c++) {
    const t = c / columns
    ctx.beginPath()
    ctx.moveTo(x0 + (x1 - x0) * 0.5, y0)
    ctx.lineTo(x0 + (x1 - x0) * t, y1)
    ctx.stroke()
  }
  for (let r = 1; r <= rows; r++) {
    // Squared spacing so the rows bunch toward the horizon, which is what sells the depth.
    const t = (r / rows) * (r / rows)
    const y = y0 + (y1 - y0) * t
    ctx.beginPath()
    ctx.moveTo(x0, y)
    ctx.lineTo(x1, y)
    ctx.stroke()
  }
}

function opChevrons(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const [x, y] = point(op['at'], env, 0.5, 0.5)
  const count = Math.min(32, Math.max(1, Math.floor(num(op['count'], 3))))
  const size = num(op['size'], 0.03) * span(env)
  ctx.strokeStyle = str(op['colour'], '#FF3FA0')
  ctx.lineWidth = num(op['width'], 4)
  for (let i = 0; i < count; i++) {
    const oy = y + i * size * 1.1
    ctx.beginPath()
    ctx.moveTo(x - size * 0.6, oy)
    ctx.lineTo(x, oy + size * 0.6)
    ctx.lineTo(x + size * 0.6, oy)
    ctx.stroke()
  }
}

function opStreaks(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const count = Math.min(400, Math.max(0, Math.floor(num(op['count'], 40))))
  const angle = num(op['angle'], 0.18)
  const dx = Math.cos(angle)
  const dy = Math.sin(angle)
  ctx.strokeStyle = str(op['colour'], '#FF3FA0')
  for (let i = 0; i < count; i++) {
    const x = env.rng.nextFloat() * env.w
    const y = env.rng.nextFloat() * env.h
    const len = (0.05 + env.rng.nextFloat() * 0.25) * env.w
    ctx.globalAlpha = 0.12 + env.rng.nextFloat() * 0.4
    ctx.lineWidth = 1 + env.rng.nextFloat() * 3
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(x + dx * len, y + dy * len)
    ctx.stroke()
  }
}

/** The alignment an op asked for, or the centring almost every one of them wants. */
function alignOf(op: Record<string, unknown>): TextAlign {
  const asked = str(op['align'], 'center')
  return asked === 'left' || asked === 'right' ? asked : 'center'
}

/**
 * The em, in pixels, for an op that states its size as a fraction of the surface's height.
 *
 * A capital then stands `size × 0.70` tall, which is the same relationship a real font has — so
 * the sizes already written into every machine definition still mean what they meant.
 *
 * `op.font` used to choose between two families and now chooses a weight: there is one face, and
 * asking for `mono` gets it drawn a little lighter rather than getting a different typeface that
 * would have to come from the device.
 */
function sizeFor(op: Record<string, unknown>, env: Env, fallback: number): number {
  return num(op['size'], fallback) * env.h
}

function weightFor(op: Record<string, unknown>, heavy: number = GLYPH_METRICS.weight): number {
  return str(op['font'], 'display') === 'display' ? heavy : GLYPH_METRICS.weight * 0.8
}

function opText(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const [x, y] = point(op['at'], env, 0.5, 0.5)
  const size = sizeFor(op, env, 0.04)
  ctx.fillStyle = str(op['colour'], '#FFFFFF')
  ctx.shadowBlur = num(op['glow'], 0)
  ctx.shadowColor = str(op['colour'], '#FFFFFF')
  glyph(ctx, env, str(op['text'], ''), x, y, size, alignOf(op), weightFor(op))
}

/**
 * Text bent around an arc, one glyph at a time.
 *
 * On a `yUp` surface each glyph is un-flipped after being placed and rotated, so the arc curves
 * the way the layout says and the letters read the way letters do.
 */
function opArcText(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const text = str(op['text'], '')
  if (text.length === 0) return
  const [cx, cy] = point(op['centre'], env, 0.5, 0.2)
  const radius = num(op['radius'], 0.3) * span(env)
  const size = num(op['size'], 0.035) * env.h
  const sweep = num(op['sweep'], Math.PI * 0.55)
  const gradient = Array.isArray(op['gradient']) ? (op['gradient'] as string[]) : undefined

  ctx.shadowBlur = num(op['glow'], 18)

  const step = text.length > 1 ? sweep / (text.length - 1) : 0
  const start = -sweep / 2
  for (let i = 0; i < text.length; i++) {
    const t = text.length > 1 ? i / (text.length - 1) : 0
    const colour = gradient !== undefined && gradient.length >= 2
      ? mixHex(gradient[0]!, gradient[gradient.length - 1]!, t)
      : str(op['colour'], '#FFFFFF')
    const a = start + step * i
    ctx.save()
    ctx.translate(cx + Math.sin(a) * radius, cy + (1 - Math.cos(a)) * radius)
    ctx.rotate(a)
    ctx.fillStyle = colour
    ctx.shadowColor = colour
    glyph(ctx, env, text[i]!, 0, 0, size)
    ctx.restore()
  }
}

function opLaneLetters(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const letters = Array.isArray(op['letters']) ? (op['letters'] as string[]) : ['N', 'O', 'V', 'A']
  const y = num(op['y'], 0.86) * env.h
  const spread = num(op['spread'], 0.33)
  const size = sizeFor(op, env, 0.022)
  ctx.fillStyle = str(op['colour'], '#3FE0FF')
  for (let i = 0; i < letters.length; i++) {
    const t = letters.length > 1 ? i / (letters.length - 1) - 0.5 : 0
    glyph(ctx, env, letters[i]!, env.w * (0.5 + t * spread), y, size)
  }
}

function opLabel(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const [x, y] = point(op['at'], env, 0.5, 0.5)
  ctx.fillStyle = str(op['colour'], '#FFB020')
  glyph(ctx, env, str(op['text'], ''), x, y, sizeFor(op, env, 0.018))
}

function opWordmark(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const [x, y] = point(op['centre'], env, 0.5, 0.5)
  const size = num(op['size'], 0.12) * env.h
  const gradient = Array.isArray(op['gradient']) ? (op['gradient'] as string[]) : ['#FF3FA0', '#3FE0FF']
  const g = ctx.createLinearGradient(x - env.w * 0.4, y, x + env.w * 0.4, y)
  g.addColorStop(0, gradient[0] ?? '#FFFFFF')
  g.addColorStop(1, gradient[gradient.length - 1] ?? '#FFFFFF')
  ctx.shadowBlur = num(op['glow'], 24)
  ctx.shadowColor = gradient[0] ?? '#FFFFFF'
  ctx.fillStyle = g
  // A wordmark is the heaviest thing on the machine: a third again over the face's own weight,
  // which is what the old `800` asked the platform font for.
  glyph(ctx, env, str(op['text'], ''), x, y, size, 'center', GLYPH_METRICS.weight * 1.35)
}

function opArrow(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const [x, y] = point(op['at'], env, 0.5, 0.5)
  const size = num(op['size'], 0.02) * span(env)
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(num(op['rotation'], 0))
  ctx.fillStyle = str(op['colour'], '#3FE0FF')
  ctx.beginPath()
  ctx.moveTo(0, -size)
  ctx.lineTo(size * 0.62, size * 0.4)
  ctx.lineTo(size * 0.24, size * 0.4)
  ctx.lineTo(size * 0.24, size)
  ctx.lineTo(-size * 0.24, size)
  ctx.lineTo(-size * 0.24, size * 0.4)
  ctx.lineTo(-size * 0.62, size * 0.4)
  ctx.closePath()
  ctx.fill()
  ctx.restore()
}

/** The unlit face of an insert. Lit is the lamp system's job, not the art program's. */
function opInsert(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const [x, y] = point(op['at'], env, 0.5, 0.5)
  const r = num(op['radius'], 0.012) * span(env)
  ctx.fillStyle = str(op['colour'], '#FFB020')
  ctx.globalAlpha = num(op['alpha'], 0.22)
  ctx.beginPath()
  ctx.arc(x, y, r, 0, Math.PI * 2)
  ctx.fill()
}

function opPanel(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const [x, y] = point(op['centre'], env, 0.5, 0.5)
  const [w, h] = pair(op['size'], 0.4, 0.2)
  ctx.fillStyle = str(op['colour'], '#0B0620')
  ctx.fillRect(x - (w * env.w) / 2, y - (h * env.h) / 2, w * env.w, h * env.h)
}

function opBorder(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const width = num(op['width'], 6)
  ctx.strokeStyle = str(op['colour'], '#8B5CF6')
  ctx.lineWidth = width
  ctx.beginPath()
  ctx.moveTo(width / 2, width / 2)
  ctx.lineTo(env.w - width / 2, width / 2)
  ctx.lineTo(env.w - width / 2, env.h - width / 2)
  ctx.lineTo(width / 2, env.h - width / 2)
  ctx.closePath()
  ctx.stroke()
}

/** The hole the dot-matrix display shows through, cut into the backglass. */
function opDmdWindow(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const [x, y] = point(op['centre'], env, 0.5, 0.8)
  const [w, h] = pair(op['size'], 0.62, 0.2)
  const px = x - (w * env.w) / 2
  const py = y - (h * env.h) / 2
  ctx.fillStyle = '#000000'
  ctx.fillRect(px, py, w * env.w, h * env.h)
  ctx.strokeStyle = str(op['colour'], '#3FE0FF')
  ctx.lineWidth = num(op['width'], 4)
  ctx.beginPath()
  ctx.moveTo(px, py)
  ctx.lineTo(px + w * env.w, py)
  ctx.lineTo(px + w * env.w, py + h * env.h)
  ctx.lineTo(px, py + h * env.h)
  ctx.closePath()
  ctx.stroke()
}

/** The maker's mark on the apron. Mirrored, because that is how it reads on a real one. */
function opMark(ctx: Canvas2D, op: Record<string, unknown>, env: Env): void {
  const [x, y] = point(op['at'], env, 0.5, 0.5)
  ctx.save()
  ctx.translate(x, y)
  if (op['mirrored'] === true) ctx.rotate(Math.PI)
  ctx.fillStyle = str(op['colour'], '#8B5CF6')
  ctx.globalAlpha = num(op['alpha'], 0.55)
  glyph(ctx, env, str(op['text'], 'HCC'), 0, 0, sizeFor(op, env, 0.3))
  ctx.restore()
}

// --- colour ---------------------------------------------------------------------------

/** Mix two `#RRGGBB` colours. Component-wise and gamma-naive, which is what a canvas does. */
export function mixHex(a: string, b: string, t: number): string {
  const pa = parseHex(a)
  const pb = parseHex(b)
  if (pa === undefined || pb === undefined) return a
  const k = t < 0 ? 0 : t > 1 ? 1 : t
  const out = [0, 1, 2].map((i) => Math.round(pa[i]! + (pb[i]! - pa[i]!) * k))
  return `#${out.map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

function parseHex(hex: string): [number, number, number] | undefined {
  if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) return undefined
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ]
}

/** Every op the engine implements. A test asserts this is exactly `ART_OPS`. */
export const IMPLEMENTED_ART_OPS: readonly string[] = Object.keys(HANDLERS)
